import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import axios from 'axios';
import {
  Building2,
  MapPin,
  Calendar,
  Search,
  ArrowRight,
  Filter,
  X,
} from 'lucide-react';
import { getListingDetailPath } from '../lib/slugUtils';
import { PageSEO } from '../components/SEO';

/**
 * Marktplatz - TasteSkill Premium Redesign
 * 
 * Verbesserungen:
 * - Hochwertigere Angebotskarten mit besserer Lesbarkeit
 * - Optimierte Filter-UI
 * - Professionelle Empty States
 * - Klickbare Gesamtkarten
 * - Hervorgehobene Kaufpreise
 */

const Marktplatz = () => {
  const [selectedIndustry, setSelectedIndustry] = useState('Alle');
  const [searchTerm, setSearchTerm] = useState('');
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/listings`);
        setListings(Array.isArray(res.data) ? res.data : []);
      } catch (error) {
        console.error('Error loading listings:', error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const industries = useMemo(
    () => ['Alle', ...new Set(listings.map((row) => row.industry).filter(Boolean))],
    [listings]
  );

  const filteredListings = listings.filter((listing) => {
    const matchesIndustry = selectedIndustry === 'Alle' || listing.industry === selectedIndustry;
    const matchesSearch =
      listing.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      listing.location?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesIndustry && matchesSearch;
  });

  const clearFilters = () => {
    setSelectedIndustry('Alle');
    setSearchTerm('');
  };

  const hasActiveFilters = selectedIndustry !== 'Alle' || searchTerm !== '';

  return (
    <>
      <PageSEO 
        title="GmbH kaufen – Marktplatz für Bestandsgesellschaften | Firmbay"
        description="Finden Sie Ihre passende Bestandsgesellschaft auf dem Firmbay-Marktplatz. Geprüfte GmbHs aus verschiedenen Branchen mit persönlicher Begleitung beim Kauf."
        path="/marktplatz"
      />
      <div className="min-h-screen bg-[#F8FAFA]">
      {/* Hero Section */}
      <section className="pt-24 pb-8 px-4 sm:px-6 lg:px-8 bg-white border-b border-gray-100">
        <div className="max-w-[1280px] mx-auto">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#E9F7F6] rounded-full mb-4">
              <div className="w-1.5 h-1.5 bg-[#009E9A] rounded-full"></div>
              <span className="text-[11px] font-semibold text-[#009E9A] uppercase tracking-wider">
                GmbH-Marktplatz
              </span>
            </div>
            <h1 className="text-[28px] md:text-[34px] font-bold text-[#0B1930] mb-3 leading-tight tracking-[-0.02em]">
              Verfügbare Bestands-GmbHs
            </h1>
            <p className="text-base text-gray-600 leading-relaxed mb-5">
              Übersicht aller verfügbaren Gesellschaften. Alle Angaben beruhen auf Informationen der jeweiligen Anbieter.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/seller-onboarding">
                <Button className="bg-[#0B1930] hover:bg-[#162844] text-white rounded-lg text-sm px-5 py-2.5">
                  GmbH verkaufen
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/kontakt">
                <Button variant="outline" className="border-gray-300 text-[#0B1930] hover:bg-[#E9F7F6] hover:border-[#009E9A] rounded-lg text-sm px-5 py-2.5">
                  Beratung anfragen
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Filters Section */}
      <section className="py-4 px-4 sm:px-6 lg:px-8 bg-white border-b border-gray-200 sticky top-[72px] z-40">
        <div className="max-w-[1280px] mx-auto">
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
            {/* Search */}
            <div className="relative w-full lg:w-80">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Suche nach Standort oder Titel..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#009E9A] focus:border-[#009E9A] bg-white"
              />
            </div>

            {/* Desktop Filters */}
            <div className="hidden lg:flex items-center gap-2 flex-wrap">
              <span className="text-sm text-gray-500 mr-1">Branche:</span>
              {industries.slice(0, 6).map(industry => (
                <button
                  key={industry}
                  onClick={() => setSelectedIndustry(industry)}
                  className={`px-3 py-2 text-sm rounded-lg transition-all ${
                    selectedIndustry === industry 
                      ? 'bg-[#0B1930] text-white' 
                      : 'bg-white border border-gray-200 text-gray-600 hover:bg-[#E9F7F6] hover:border-[#009E9A]'
                  }`}
                >
                  {industry}
                </button>
              ))}
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="px-3 py-2 text-sm text-gray-500 hover:text-[#0B1930] flex items-center gap-1"
                >
                  <X className="w-3 h-3" />
                  Zurücksetzen
                </button>
              )}
            </div>

            {/* Mobile Filter Toggle */}
            <button
              onClick={() => setShowMobileFilters(!showMobileFilters)}
              className="lg:hidden flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-600"
            >
              <Filter className="w-4 h-4" />
              Filter
              {hasActiveFilters && (
                <span className="w-5 h-5 bg-[#009E9A] text-white rounded-full text-xs flex items-center justify-center">
                  {(selectedIndustry !== 'Alle' ? 1 : 0) + (searchTerm ? 1 : 0)}
                </span>
              )}
            </button>
          </div>

          {/* Mobile Filters */}
          {showMobileFilters && (
            <div className="lg:hidden mt-4 pt-4 border-t border-gray-100">
              <div className="flex flex-wrap gap-2">
                <span className="text-sm text-gray-500 w-full mb-2">Branche:</span>
                {industries.map(industry => (
                  <button
                    key={industry}
                    onClick={() => setSelectedIndustry(industry)}
                    className={`px-3 py-2 text-sm rounded-lg transition-all ${
                      selectedIndustry === industry 
                        ? 'bg-[#0B1930] text-white' 
                        : 'bg-white border border-gray-200 text-gray-600'
                    }`}
                  >
                    {industry}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Results Count */}
      <section className="pt-6 pb-2 px-4 sm:px-6 lg:px-8">
        <div className="max-w-[1280px] mx-auto">
          <p className="text-sm text-gray-500">
            {loading ? 'Lade Angebote...' : `${filteredListings.length} ${filteredListings.length === 1 ? 'Gesellschaft' : 'Gesellschaften'} gefunden`}
          </p>
        </div>
      </section>

      {/* Listings Section */}
      <section className="pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-[1280px] mx-auto">
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="bg-white border border-gray-200 rounded-xl p-6 animate-pulse">
                  <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
                  <div className="h-4 bg-gray-200 rounded w-2/3 mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          ) : filteredListings.length === 0 ? (
            /* Empty State */
            <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
              <div className="w-16 h-16 bg-[#E9F7F6] rounded-full flex items-center justify-center mx-auto mb-4">
                <Building2 className="w-8 h-8 text-[#009E9A]" />
              </div>
              <h3 className="text-lg font-bold text-[#0B1930] mb-2">
                Keine Gesellschaften gefunden
              </h3>
              <p className="text-sm text-gray-600 mb-6 max-w-md mx-auto">
                {hasActiveFilters 
                  ? 'Für Ihre Filterkriterien sind derzeit keine Gesellschaften verfügbar. Versuchen Sie andere Filter.'
                  : 'Derzeit sind keine Gesellschaften im Marktplatz verfügbar. Schauen Sie später wieder vorbei.'}
              </p>
              {hasActiveFilters && (
                <Button
                  onClick={clearFilters}
                  variant="outline"
                  className="border-gray-300 text-[#0B1930] hover:bg-[#E9F7F6]"
                >
                  Filter zurücksetzen
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredListings.map(listing => (
                <Link
                  key={listing.id}
                  to={getListingDetailPath(listing)}
                  className="block bg-white border border-gray-200 rounded-xl hover:border-[#009E9A] hover:shadow-md transition-all group"
                  data-testid={`listing-card-${listing.id}`}
                >
                  <div className="p-5 md:p-6">
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                      {/* Main Info */}
                      <div className="flex-1 min-w-0">
                        {/* Status & Industry */}
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded ${
                            listing.status === 'Verfügbar' 
                              ? 'bg-[#E9F7F6] text-[#009E9A]' 
                              : 'bg-gray-100 text-gray-500'
                          }`}>
                            {listing.status}
                          </span>
                          <span className="text-xs text-gray-500 bg-gray-100 px-2.5 py-1 rounded">
                            {listing.industry}
                          </span>
                        </div>
                        
                        {/* Title */}
                        <h3 className="text-lg md:text-xl font-bold text-[#0B1930] mb-2 group-hover:text-[#009E9A] transition-colors">
                          {listing.title}
                        </h3>
                        
                        {/* Description */}
                        {listing.description && (
                          <p className="text-sm text-gray-600 mb-4 line-clamp-2 leading-relaxed">
                            {listing.description}
                          </p>
                        )}

                        {/* Meta Info */}
                        <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-gray-600">
                          <span className="flex items-center gap-1.5">
                            <MapPin className="w-4 h-4 text-gray-400" />
                            {listing.location}
                          </span>
                          <span className="flex items-center gap-1.5">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            Gründung {listing.founded_year}
                          </span>
                          {listing.stammkapital && (
                            <span className="flex items-center gap-1.5">
                              <Building2 className="w-4 h-4 text-gray-400" />
                              {listing.stammkapital.toLocaleString('de-DE')} € Stammkapital
                            </span>
                          )}
                        </div>

                        {/* Features */}
                        {listing.features && listing.features.length > 0 && (
                          <div className="mt-4 flex flex-wrap gap-2">
                            {listing.features.slice(0, 4).map((feature, idx) => (
                              <span key={idx} className="text-xs text-gray-600 bg-[#F8FAFA] px-2.5 py-1 rounded">
                                {feature}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Price & CTA */}
                      <div className="lg:text-right lg:flex-shrink-0 lg:ml-6 flex flex-row lg:flex-col items-center lg:items-end justify-between lg:justify-start gap-4">
                        <div>
                          <div className="text-[10px] text-gray-400 uppercase tracking-wide mb-0.5">Kaufpreis</div>
                          <div className="text-2xl md:text-[26px] font-bold text-[#0B1930]">
                            {listing.price?.toLocaleString('de-DE')} €
                          </div>
                        </div>
                        <Button
                          className="bg-[#0B1930] hover:bg-[#162844] text-white text-sm rounded-lg px-5 py-2.5 group-hover:bg-[#009E9A]"
                          disabled={listing.status !== 'Verfügbar'}
                        >
                          Details ansehen
                          <ArrowRight className="ml-2 w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Info Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white border-t border-gray-100">
        <div className="max-w-[1000px] mx-auto">
          <h2 className="text-xl md:text-2xl font-bold text-[#0B1930] mb-6">
            Hinweise zu Bestands-GmbHs
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {[
              {
                title: 'Bereits bestehende Gesellschaften',
                text: 'Je nach Gesellschaft können Handelsregistereintragung, Steuernummer, USt-IdNr., Bankverbindung oder weitere Strukturen bereits vorhanden sein.'
              },
              {
                title: 'Bestehende Unternehmenshistorie',
                text: 'Bei älteren Gesellschaften können bereits Jahresabschlüsse, Registerhistorie und weitere Unternehmensdaten vorliegen.'
              },
              {
                title: 'Vermeidung einzelner Gründungsschritte',
                text: 'Der Erwerb einer bereits bestehenden Gesellschaft kann gegenüber einer Neugründung einzelne Gründungs- und Eintragungsschritte vermeiden.'
              },
              {
                title: 'Vorgeprüfte Gesellschaftsangebote',
                text: 'Die vom Anbieter bereitgestellten Angaben werden im Rahmen unseres Vermittlungsprozesses auf Vollständigkeit und Plausibilität geprüft.'
              }
            ].map((item, idx) => (
              <div key={idx}>
                <h3 className="font-semibold text-[#0B1930] mb-1.5 text-[15px]">{item.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>

          <div className="p-5 border border-gray-200 rounded-lg bg-[#F8FAFA]">
            <p className="text-sm text-gray-600 leading-relaxed">
              <strong className="text-[#0B1930]">Hinweis:</strong> Die Prüfung erfolgt ausschließlich auf Grundlage der uns zur Verfügung gestellten 
              Informationen und Unterlagen. Firmbay übernimmt keine Gewähr für deren Vollständigkeit oder Richtigkeit 
              und führt keine Rechts-, Steuer- oder Wirtschaftsprüfung (Due Diligence) durch. Interessenten sollten 
              die Gesellschaft vor Erwerb durch entsprechend qualifizierte Berater prüfen lassen.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-[#0B1930]">
        <div className="max-w-[700px] mx-auto text-center">
          <h2 className="text-xl md:text-2xl font-bold text-white mb-3">
            Interesse an einer Gesellschaft?
          </h2>
          <p className="text-gray-300 mb-6">
            Kontaktieren Sie uns für weitere Informationen und eine persönliche Begleitung.
          </p>
          <Link to="/kontakt">
            <Button className="bg-white text-[#0B1930] hover:bg-[#E9F7F6] rounded-lg text-sm px-6 py-2.5">
              Kontakt aufnehmen
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
    </>
  );
};

export default Marktplatz;
